import axios from 'axios';
import EventBus from 'eventing-bus';
import { ApiUrl } from './config';
import { extractWorkspaceId } from '../utils/workspaceUtils';

axios.defaults.baseURL = ApiUrl;
axios.defaults.headers.common['Content-Type'] = `application/json`;
let token = localStorage.getItem('token');
if (token) axios.defaults.headers.common['Authorization'] = `bearer ${token}`;

export const setToken = (token) => {
  if (token) axios.defaults.headers.common['Authorization'] = `bearer ${token}`;
  else delete axios.defaults.headers.common['Authorization'];
};

export const setHash = hash => {
  if (hash) axios.defaults.headers.common['hash'] = hash;
  else delete axios.defaults.headers.common['hash'];
};

export const setWorkspaceId = (workspaceId) => {
  if (workspaceId) axios.defaults.headers.common['x-workspace-id'] = workspaceId;
  else delete axios.defaults.headers.common['x-workspace-id'];
};

// Add request interceptor to ensure workspace ID is always included
axios.interceptors.request.use(
  (config) => {
    // If x-workspace-id is not already set, try to extract from current URL
    if (!config.headers['x-workspace-id']) {
      const currentPath = window.location.pathname;
      const workspaceId = extractWorkspaceId(currentPath);

      if (workspaceId) config.headers['x-workspace-id'] = workspaceId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle permission errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if response has a 'modal' type - route to modal instead of toast
    if (error.response && error.response.data && error.response.data.type === 'modal') {
      const errorMessage = error.response.data?.message || error.response.data?.body || 'An error occurred';

      // Publish modal error event for display in popup
      EventBus.publish('error:modal', { status: error.response.status, message: errorMessage, data: error.response.data });

      // Return a special resolved response so saga knows modal was shown
      // This prevents saga from publishing toast error
      return Promise.resolve({
        data: { code: error.response.status, message: errorMessage, body: [], _modalHandled: true }
      });
    }

    // Check if it's a payment required error (501/402)
    if (error.response && (error.response.status === 501 || error.response.status === 402)) {
      const errorData = error.response.data;
      const errorMessage = errorData?.message || errorData?.body?.message || 'Payment required';

      // Don't show toast - user already sees banner and modal
      // Just publish event for internal handling
      EventBus.publish('error:payment-required', {
        status: error.response.status,
        message: errorMessage,
        data: errorData
      });
    }

    // Check if it's an authentication error (401) for user paused/deactivated
    if (error.response && error.response.status === 401) {
      const errorMessage = error.response.data?.message || error.response.data?.body || 'Unauthorized';

      // Check if this is a user paused/deactivated error - show modal instead of logout
      const isUserStatusError = errorMessage.includes('account has been paused') || errorMessage.includes('account has been deactivated');

      if (isUserStatusError) {
        // Publish error to show modal (using same event as 403 permission errors)
        EventBus.publish('error:403', errorMessage);
        return Promise.resolve({ data: { code: 401, message: errorMessage, body: [], _modalHandled: true } });
      }

      // For other 401 errors (expired token, etc.), let normal flow continue
    }

    // Check if it's a permission error (403)
    if (error.response && error.response.status === 403) {
      const errorMessage = error.response.data?.message || error.response.data?.body || 'Permission denied';

      // Check if this is a workspace paused/deactivated error
      // These should ONLY show modal, not toast
      const isWorkspaceStatusError = errorMessage.includes('workspace has been paused') || errorMessage.includes('workspace has been deactivated');
      if (isWorkspaceStatusError) {
        EventBus.publish('error:403', errorMessage);
        return Promise.resolve({ data: { code: 403, message: errorMessage, body: [], _modalHandled: true } });
      }

      // For other 403 errors, publish both modal and allow normal error flow
      EventBus.publish('error:403', errorMessage);
      EventBus.publish('api:error', {
        status: 403,
        message: errorMessage,
        data: error.response.data
      });
    }

    // Check for 404 errors that might be permission-related
    if (error.response && error.response.status === 404) {
      const errorMessage = error.response.data?.message || error.response.data?.body || 'Not found';
      const requestUrl = error.config?.url || '';

      console.log('404 error intercepted, URL:', requestUrl);

      // Detect if this 404 might be a permission issue
      // Look for object access patterns like /api/objects/{type}/{id} or /objects/{type}/{id}
      // The URL might not have /api prefix if baseURL is already set
      const objectAccessPattern = /(?:\/api)?\/objects\/\w+\/[a-f0-9]{24}/i;
      const isObjectAccess = objectAccessPattern.test(requestUrl);

      console.log('Is object access URL?', isObjectAccess, 'Pattern test on:', requestUrl);

      if (isObjectAccess) {
        // This looks like an object access that returned 404
        console.log('Publishing api:potential-permission-404 event');
        EventBus.publish('api:potential-permission-404', {
          status: 404,
          message: errorMessage,
          url: requestUrl,
          data: error.response.data
        });
      }
    }

    return Promise.reject(error);
  }
);

// Initialize workspace ID from URL on load
const initWorkspaceId = () => {
  const currentPath = window.location.pathname;
  const workspaceId = extractWorkspaceId(currentPath);

  if (workspaceId) {
    setWorkspaceId(workspaceId);
  }
};

// Initialize workspace ID immediately
initWorkspaceId();
export default axios;